import { IProgram, IUser } from '@types';
import {
  DATA_TYPE_PERMISSIONS,
  PERMISSION_PRODUCTS,
  USER_ROLES
} from 'shared/enums';
import { hasAdminRole } from 'shared/helpers/hasAdminRole';

interface IShowAnalyticsPrompt {
  user: IUser;
  programs: IProgram[];
}

/**
 * This helper takes a user object and an array of programs, and returns a boolean indicating whether the analytics prompt should be shown.
 *
 * Returns true ONLY in the following case:
 * 1. The user is an ADMIN OR a SCHOOL_ADMIN that has access to analytics in at least one program in the provided programs array.
 * AND
 * 2. One of the provided programs has analytics.
 */
export const showAnalyticsPrompt = ({
  user,
  programs
}: IShowAnalyticsPrompt): boolean => {
  const userIsAdmin = hasAdminRole(user);
  const userIsSchoolAdmin = user?.roles?.includes(USER_ROLES.SCHOOL_ADMIN);

  const programsWithAnalytics = programs
    ?.filter(program =>
      program?.products?.includes(PERMISSION_PRODUCTS.ANALYTICS)
    )
    ?.map(program => program.value);

  const userAnalyticsPrograms = user?.schoolPermissions
    ?.filter(schoolPermissionObject =>
      schoolPermissionObject.keys.some(key =>
        (DATA_TYPE_PERMISSIONS as string[]).includes(key)
      )
    )
    ?.map(program => program.schoolId);

  const userHasAnalyticsInProvidedPrograms = userAnalyticsPrograms?.some(
    program => programsWithAnalytics?.includes(program)
  );

  return (
    (userIsAdmin && programsWithAnalytics?.length > 0) ||
    (userIsSchoolAdmin && userHasAnalyticsInProvidedPrograms)
  );
};
